"use client";

import { useState } from "react";
import Link from "next/link";

import {
  EventSnapshot,
  conciergeHrefFor,
  conciergePromptFor,
  formatDistance,
  formatEventDate,
  formatEventDateShort,
  formatEventTime,
  hotelDistance,
  hotelsByDistance,
  isHeroImage,
  stayShape,
  ticketPriceText,
  venueCityPath,
} from "@/lib/eventDealsShared";
import { HotelSummary } from "@/generated/search.openapi";
import HotelCard from "@/components/section/card/HotelCard";
import { GoogleAuthModal } from "@/components/modal/googleAuthModal";
import AboutGondolaModal from "@/components/modal/AboutGondolaModal";
import { H1, H4 } from "@/components/ui/typography";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

type ChainFilter = {
  key: string;
  displayName: string;
  matches: (hotel: HotelSummary) => boolean;
};

const CHAIN_FILTERS: ChainFilter[] = [
  { key: "all", displayName: "All", matches: () => true },
  {
    key: "hilton",
    displayName: "Hilton",
    matches: (hotel) => hotel.chainName === "Hilton",
  },
  {
    key: "marriott",
    displayName: "Marriott",
    matches: (hotel) => hotel.chainName === "Marriott",
  },
  {
    key: "hyatt",
    displayName: "Hyatt",
    matches: (hotel) => hotel.chainName === "Hyatt",
  },
  {
    key: "ihg",
    displayName: "IHG",
    matches: (hotel) => hotel.chainName === "IHG",
  },
];

type SortKey = "value" | "distance";

type Props = {
  snapshot: EventSnapshot;
};

export default function EventHotelsContent({ snapshot }: Props) {
  const [selectedFilter, setSelectedFilter] = useState<ChainFilter>(
    CHAIN_FILTERS[0]
  );
  const [sortBy, setSortBy] = useState<SortKey>("value");
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const availableFilters = CHAIN_FILTERS.filter(
    (filter) =>
      filter.key === "all" ||
      snapshot.hotels.some((hotel) => filter.matches(hotel))
  );
  const ordered =
    sortBy === "distance" ? hotelsByDistance(snapshot) : snapshot.hotels;
  const filteredHotels = ordered.filter((hotel) =>
    selectedFilter.matches(hotel)
  );
  const venue = snapshot.venue.name ?? snapshot.city_name;
  const hasDistances = Object.keys(snapshot.distances).length > 0;
  const time = formatEventTime(snapshot.local_time);
  const price = ticketPriceText(snapshot.price_range);
  const genre =
    snapshot.classification.genre ?? snapshot.classification.segment;
  const hubPath = snapshot.venue_path;
  const when = [
    formatEventDate(snapshot.local_date),
    time,
    [snapshot.venue.city, snapshot.venue.state_code].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");
  // A hero-sized photo stretches above the title; a small or unsized one (some events only
  // carry a 205px crop, off-host images have no dimensions) sits beside the event details.
  const heroImage = isHeroImage(snapshot.image) ? snapshot.image : null;
  const compactImage = snapshot.image && !heroImage ? snapshot.image : null;

  return (
    <main className="z-10 mx-5 flex flex-col gap-8 bg-background pb-16 lg:mx-10 2xl:mx-20">
      {/* Hero */}
      <div className="flex flex-col gap-4 pt-6">
        {heroImage ? (
          <img
            src={heroImage.url}
            alt={`${snapshot.name} at ${venue}`}
            width={heroImage.width ?? undefined}
            height={heroImage.height ?? undefined}
            loading="eager"
            decoding="async"
            className="mx-auto max-h-64 w-full max-w-3xl rounded-lg object-cover"
          />
        ) : null}
        <H1 className="text-linear-gradient-default text-center font-medium">
          Hotels Near{" "}
          {hubPath ? (
            <Link
              href={hubPath}
              className="italic underline-offset-4 hover:underline"
            >
              {venue}
            </Link>
          ) : (
            <span className="italic">{venue}</span>
          )}{" "}
          for {snapshot.name}
        </H1>
        {compactImage ? (
          <Card className="mx-auto h-auto max-w-xl justify-start gap-0 rounded-lg px-3 py-3 sm:px-3 sm:py-3 md:py-3 lg:py-3">
            <CardContent className="flex items-center gap-4 p-0">
              <img
                src={compactImage.url}
                alt={`${snapshot.name} at ${venue}`}
                width={compactImage.width ?? undefined}
                height={compactImage.height ?? undefined}
                loading="eager"
                decoding="async"
                className="h-20 w-36 shrink-0 rounded-md object-cover sm:h-24 sm:w-44"
              />
              <div className="flex min-w-0 flex-col gap-1 text-left">
                <span className="font-medium text-foreground">
                  {snapshot.name}
                </span>
                <span className="text-sm text-muted-foreground">{when}</span>
                <span className="text-xs text-muted-foreground">
                  {[venue, snapshot.venue.address_line_1]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <H4 className="mx-auto max-w-3xl text-center font-normal text-muted-foreground">
            {when}
          </H4>
        )}
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground">
          {genre ? <span>{genre}</span> : null}
          {price ? <span>{price}</span> : null}
          <a
            href={snapshot.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline transition-colors hover:text-foreground"
          >
            Get tickets on Ticketmaster
          </a>
        </div>
        <p className="mx-auto max-w-3xl text-center text-sm text-muted-foreground">
          {snapshot.description} Prices below are for the night of the event,
          with the walk to {venue} on every hotel. Want advanced search?{" "}
          <Button
            variant="link"
            size="none"
            className="italic underline"
            onClick={() => setIsAboutModalOpen(true)}
          >
            Learn more
          </Button>
        </p>
      </div>

      {/* Chain filter pills and sort */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {availableFilters.length > 1 &&
          availableFilters.map((filter) => (
            <Button
              key={filter.key}
              variant={
                selectedFilter.key === filter.key ? "default" : "outline"
              }
              size="sm"
              onClick={() => setSelectedFilter(filter)}
            >
              {filter.displayName}
            </Button>
          ))}
        {hasDistances && (
          <>
            <span className="mx-2 hidden text-muted-foreground sm:inline">
              ·
            </span>
            <Button
              variant={sortBy === "value" ? "default" : "outline"}
              size="sm"
              onClick={() => setSortBy("value")}
            >
              Best value
            </Button>
            <Button
              variant={sortBy === "distance" ? "default" : "outline"}
              size="sm"
              onClick={() => setSortBy("distance")}
            >
              Closest
            </Button>
          </>
        )}
      </div>

      {/* Hotel cards grid: each card links with its own rate's checkin/checkout strings
          (the event night, or the night before for a 2-night stay). */}
      {filteredHotels.length > 0 ? (
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))",
          }}
        >
          {filteredHotels.map((hotel, index) => {
            const distance = hotelDistance(snapshot, hotel);
            const stay = stayShape(hotel);
            return (
              <div
                key={hotel.vervotechPropertyId ?? index}
                className="flex flex-col gap-1"
              >
                <HotelCard
                  hotel={hotel}
                  className="w-full"
                  showDate
                  showDateRangeTag
                />
                {distance || stay ? (
                  <p className="px-1 text-xs text-muted-foreground">
                    {distance ? `${formatDistance(distance)} to ${venue}` : ""}
                    {distance && stay ? " · " : ""}
                    {stay ?? ""}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-20 text-center text-muted-foreground">
          No {selectedFilter.displayName} hotels near {venue} for this night.
          Try another chain.
        </div>
      )}

      {/* Listings collapsed into this page */}
      {(snapshot.also_at_venue.length > 0 ||
        snapshot.other_dates.length > 0) && (
        <section className="mx-auto flex w-full max-w-3xl flex-col gap-4 text-sm">
          {snapshot.also_at_venue.length > 0 && (
            <div className="flex flex-col gap-1">
              <h2 className="font-semibold text-foreground">
                Also at {venue} that night
              </h2>
              <ul className="flex flex-col gap-1 text-muted-foreground">
                {snapshot.also_at_venue.map((listing) => {
                  const listingTime = formatEventTime(listing.local_time);
                  return (
                    <li key={`${listing.name}-${listing.url}`}>
                      <a
                        href={listing.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline transition-colors hover:text-foreground"
                      >
                        {listing.name}
                      </a>
                      {listingTime ? ` · ${listingTime}` : ""}
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-muted-foreground">
                Same venue, same night, same hotels. Book from this page for any
                of them.
              </p>
            </div>
          )}
          {snapshot.other_dates.length > 0 && (
            <div className="flex flex-col gap-1">
              <h2 className="font-semibold text-foreground">
                Other dates for {snapshot.name}
              </h2>
              <p className="text-muted-foreground">
                {snapshot.other_dates.map(formatEventDateShort).join(", ")}
                {hubPath ? (
                  <>
                    . For rates on those nights, start from the{" "}
                    <Link href={hubPath} className="underline">
                      {venue} hotels page
                    </Link>
                    .
                  </>
                ) : (
                  "."
                )}
              </p>
            </div>
          )}
        </section>
      )}

      {/* Cross-links */}
      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
        {hubPath ? (
          <Link
            href={hubPath}
            className="underline transition-colors hover:text-foreground"
          >
            All hotels and events near {venue}
          </Link>
        ) : null}
        <Link
          href={venueCityPath(snapshot.city_slug)}
          className="underline transition-colors hover:text-foreground"
        >
          More venues and events in {snapshot.city_name}
        </Link>
        <Link
          href={`/deals/${snapshot.city_slug}`}
          className="underline transition-colors hover:text-foreground"
        >
          {snapshot.city_name} hotel points deals by month
        </Link>
      </div>

      {/* Concierge handoff: prefills the planner composer; nothing is sent until the user does. */}
      <div className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center">
        <h2 className="text-xl font-semibold text-foreground">
          Plan this trip with Concierge
        </h2>
        <p className="text-sm text-muted-foreground">
          Tell Concierge your points balances and it will find the best way to
          book this night near {venue}.
        </p>
        <Link
          href={conciergeHrefFor(
            conciergePromptFor({
              venueName: snapshot.venue.name,
              cityName: snapshot.city_name,
              date: snapshot.local_date,
              eventName: snapshot.name,
            })
          )}
          className={buttonVariants({ className: "px-8 py-3" })}
        >
          Open Concierge
        </Link>
      </div>

      {/* CTA */}
      <div className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center">
        <h2 className="text-xl font-semibold text-foreground">
          Search every hotel in {snapshot.city_name}
        </h2>
        <p className="text-sm text-muted-foreground">
          Create a free account to compare points and cash on every hotel near
          the venue and pick the best value for your stay.
        </p>
        <Button onClick={() => setIsLoginModalOpen(true)} className="px-8 py-3">
          Get Started Free
        </Button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Last updated{" "}
        {new Date(snapshot.generated_at).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        })}
      </p>

      {/* Modals */}
      <AboutGondolaModal
        open={isAboutModalOpen}
        onOpenChange={setIsAboutModalOpen}
        onClick={() => {
          setIsAboutModalOpen(false);
          setIsLoginModalOpen(true);
        }}
      />
      <GoogleAuthModal
        open={isLoginModalOpen}
        onOpenChange={setIsLoginModalOpen}
        headerText="Create an account"
        callbackUrl="/loyalty-dashboard"
      />
    </main>
  );
}
